/**
 * Левое меню (docs/VISUAL-REFERENCE.md §1).
 *
 * Верхние вкладки заменены вертикальным меню: разделов семь, в строку они
 * читались как мелкая лента, а слева у каждого есть иконка и воздух.
 * Схлопывается до иконок — состояние помнится между заходами.
 */
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  IconBook, IconChevronLeft, IconChevronRight, IconColumns, IconFields,
  IconHistory, IconInbox, IconSettings, IconUsers,
} from '../components/icons.js'
import { Tooltip } from '../components/index.js'
import { texts } from '../texts.js'
import type { PermissionCode } from '@shared/types.ts'
import './Sidebar.css'

const STORAGE_KEY = 'bm.sidebar'

export interface NavItem {
  to: string
  label: string
  icon: JSX.Element
  permission?: PermissionCode | PermissionCode[]
  match?: (path: string) => boolean
}

/**
 * Разделы по правам. Список общий с меню телефона (бургер в шапке): два
 * набора пунктов неминуемо разъехались бы.
 */
export function navItems(
  channel: string,
  can: (permission: PermissionCode) => boolean,
  loading: boolean,
): NavItem[] {
  const items: NavItem[] = [
    { to: `/brands?ch=${channel}`, label: texts.topbar.nav.brands, icon: <IconColumns size={20} />, match: (p) => p.startsWith('/brands') },
    { to: '/digest', label: 'Сводка', icon: <IconInbox size={20} />, permission: 'history.view' },
    { to: '/history', label: texts.topbar.nav.history, icon: <IconHistory size={20} />, permission: 'history.view' },
    { to: '/catalog/categories', label: texts.topbar.nav.catalog, icon: <IconBook size={20} />, permission: ['catalog.categories.manage', 'catalog.enums.manage'], match: (p) => p.startsWith('/catalog') },
    { to: '/fields', label: texts.topbar.nav.fields, icon: <IconFields size={20} /> },
    { to: '/users', label: 'Пользователи', icon: <IconUsers size={20} />, permission: 'users.manage' },
    /* «Настройки» в меню нет нарочно: экраном не пользуются, а поменять
       настройку проще прямым запросом. Сам экран жив — он открывается
       по адресу /settings. */
  ]

  return items.filter((item) => {
    if (loading) return item.to.startsWith('/brands')
    if (!item.permission) return true
    const list = Array.isArray(item.permission) ? item.permission : [item.permission]
    return list.some((permission) => can(permission))
  })
}

export interface SidebarProps {
  channel: string
  loading: boolean
  can: (permission: PermissionCode) => boolean
  pathname: string
}

function readCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'collapsed') return true
    if (stored === 'expanded') return false
  } catch {
    /* приватный режим — переживём */
  }
  // По умолчанию свёрнуто: инструмент про таблицу, а не про навигацию.
  return true
}

export function Sidebar({ channel, loading, can, pathname }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? 'collapsed' : 'expanded')
    } catch {
      /* ничего страшного */
    }
  }, [collapsed])

  const allowed = navItems(channel, can, loading)

  return (
    <aside className={collapsed ? 'sidebar sidebar--collapsed' : 'sidebar'}>
      {/* Логотипа здесь больше нет: он стоит в верхней строке, слева. Два
          логотипа в одном углу — это не узнаваемость, а дубль. */}
      <nav className="sidebar__nav" aria-label={texts.topbar.nav.label}>
        {allowed.map((item) => {
          const active = item.match ? item.match(pathname) : pathname.startsWith(item.to.split('?')[0] ?? item.to)
          const link = (
            <NavLink
              key={item.to}
              className={active ? 'sidebar__item sidebar__item--active' : 'sidebar__item'}
              to={item.to}
              /* В свёрнутом меню от пункта остаётся один значок. Имя не может
                 держаться на подсказке: без неё ссылка безымянна и для
                 скринридера, и для наведения мышью. */
              aria-label={collapsed ? item.label : undefined}
              title={collapsed ? item.label : undefined}
              aria-current={active ? 'page' : undefined}
            >
              <span className="sidebar__icon" aria-hidden="true">{item.icon}</span>
              {!collapsed && <span className="sidebar__label">{item.label}</span>}
            </NavLink>
          )
          return collapsed
            ? <Tooltip key={item.to} content={item.label} placement="bottom-start">{link}</Tooltip>
            : link
        })}
      </nav>

      <button
        type="button"
        className="sidebar__collapse"
        onClick={() => setCollapsed((value) => !value)}
        aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
      >
        <span className="sidebar__icon" aria-hidden="true">
          {collapsed ? <IconChevronRight size={20} /> : <IconChevronLeft size={20} />}
        </span>
        {!collapsed && <span className="sidebar__label">Свернуть</span>}
      </button>
    </aside>
  )
}
